/** Shared helpers for claim service-history rows */

/**
 * Calendar years + months between DOJ and DOL.
 * Months are always 0–11 (excess rolls into years).
 * Any leftover days count as one additional month (then normalized).
 */
export const getServiceDurationParts = (doj, dol) => {
  if (!doj || !dol) return null;
  const startDate = new Date(doj);
  const endDate = new Date(dol);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
    return null;
  }

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  const days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
  } else if (days > 0) {
    months += 1;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (months >= 12) {
    years += Math.floor(months / 12);
    months %= 12;
  }

  if (years < 0) return null;
  return { years, months };
};

/** e.g. "3.8 Years" = 3 years + 8 months (months always 0–11) */
export const formatServiceDuration = (parts) => {
  if (!parts) return '';
  const y = Math.max(0, Math.floor(Number(parts.years) || 0));
  let m = Math.max(0, Math.floor(Number(parts.months) || 0));
  const extraYears = Math.floor(m / 12);
  m %= 12;
  const totalYears = y + extraYears;
  return `${totalYears}.${m} Years`;
};

export const calculateServiceYears = (doj, dol) => {
  const parts = getServiceDurationParts(doj, dol);
  return parts ? formatServiceDuration(parts) : '';
};

/** Sum all employment rows → years + months (0–11) for pension eligibility */
export const sumServiceDuration = (history = []) => {
  let totalMonths = 0;
  (history || []).forEach((row) => {
    const parts = getServiceDurationParts(row?.doj, row?.dol);
    if (!parts) return;
    totalMonths += parts.years * 12 + parts.months;
  });
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return { years, months, totalMonths };
};

export const formatTotalServiceLabel = (history = []) => {
  const parts = sumServiceDuration(history);
  if (!parts.totalMonths) return null;
  return formatServiceDuration(parts);
};

/** First letter of each word capital, rest lower — e.g. LLP → Llp */
export const toTitleCaseWords = (value) => {
  if (value == null || value === '') return '';
  return String(value)
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const dateTime = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Older employment first; rows without DOJ sink to the end */
export const sortServiceHistory = (rows) => {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const da = dateTime(a?.doj);
    const db = dateTime(b?.doj);
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    if (da !== db) return da - db;
    const ea = dateTime(a?.dol) ?? 0;
    const eb = dateTime(b?.dol) ?? 0;
    return ea - eb;
  });
};

const isYes = (value) => String(value || '').trim().toLowerCase() === 'yes';

/**
 * Contribution totals minus claimed:
 * Form-19 Yes → PF/EPF claimed for that row
 * Form-10C Yes → EPS claimed for that row
 */
export const computeContributionBalances = (history = []) => {
  let pfContribution = 0;
  let epsContribution = 0;
  let pfClaimed = 0;
  let epsClaimed = 0;

  history.forEach((entry) => {
    const pf = parseFloat(entry?.pf_epf_balance) || 0;
    const eps = parseFloat(entry?.eps_balance) || 0;
    pfContribution += pf;
    epsContribution += eps;
    if (isYes(entry?.form_19)) pfClaimed += pf;
    if (isYes(entry?.form_10c_scheme_certificate)) epsClaimed += eps;
  });

  return {
    pfContribution,
    epsContribution,
    pfClaimed,
    epsClaimed,
    pfBalance: Math.max(0, pfContribution - pfClaimed),
    epsBalance: Math.max(0, epsContribution - epsClaimed),
    totalBalance: Math.max(0, pfContribution - pfClaimed) + Math.max(0, epsContribution - epsClaimed)
  };
};

export const normalizeServiceHistoryEntry = (entry = {}) => {
  const doj = entry.doj || '';
  const dol = entry.dol || '';
  const form13Raw = String(entry.form_13 || entry.form13 || 'NA').trim().toLowerCase();
  const form13 = form13Raw === 'yes' ? 'Yes' : form13Raw === 'no' ? 'No' : 'NA';
  return {
    ...entry,
    company_name: toTitleCaseWords(entry.company_name || ''),
    doj,
    dol,
    form_13: form13,
    service: doj && dol ? calculateServiceYears(doj, dol) : (entry.service || '')
  };
};

/** Form-13 = No → transfer still required */
export const countTransfersNeededFromHistory = (history = []) =>
  (history || []).filter(
    (row) => String(row?.form_13 || '').trim().toLowerCase() === 'no'
  ).length;

